import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/src/renderer/components/ui/select'
import { Switch } from '@/src/renderer/components/ui/switch'
import { Button } from '@/src/renderer/components/ui/Button'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import { getPlatformAPI, getPlatformInfo, type ReminderPermission } from '@/src/renderer/lib/platform'
import {
  REMINDER_DEFAULT_OFFSET, REMINDER_DEFAULT_OFFSET_KEY, type ReminderDefaultOffset,
} from '@/src/renderer/lib/reminders/preference'
import { SettingsSection, SettingsRow } from '../../primitives'

/** Die Wahl für die Standard-Erinnerung, wie im Prototyp. */
const OFFSETS: ReadonlyArray<ReminderDefaultOffset> = ['off', 0, 5, 10, 15, 30, 60]

/**
 * **Erinnerungen** in „App-Verhalten“ (Plan task-reminders, Etappe 5) — auf beiden Plattformen:
 *
 * - **Standard-Erinnerung**: ihr Vorlauf, synchronisiert; gilt für alle Standard-Erinnerungen sofort.
 * - **Auf diesem Gerät erinnern**: nur dieses Gerät, nicht synchronisiert.
 * - Im Browser: ob er Mitteilungen zeigen darf, mit „Erlauben“.
 * - **Test**: eine Mitteilung, um zu sehen, ob sie ankommt — und wo man nachsieht, wenn nicht.
 *
 * Unter „Auf diesem Gerät“ steht, wann HappyTask laufen muss: kein Autostart (Entscheidung 11),
 * im Web nur mit offenem Tab (Entscheidung 10).
 */
export function ReminderSection({ platform }: { platform: 'electron' | 'web' }) {
  const { t } = useTranslation()
  const [offset, setOffset] = useDataSource().usePreference<ReminderDefaultOffset>(REMINDER_DEFAULT_OFFSET_KEY, REMINDER_DEFAULT_OFFSET)
  const delivery = getPlatformAPI().reminders
  const system = platform === 'web' ? 'web' : getPlatformInfo().isMac ? 'mac' : 'windows'
  const [enabled, setEnabled] = useState(true)
  /** Schon geschaltet — dann gilt die Wahl, nicht der gespeicherte Wert, der erst danach ankommt. */
  const touched = useRef(false)
  const [permission, setPermission] = useState<ReminderPermission | undefined>(() =>
    platform === 'web' ? delivery?.getPermission() : undefined)

  useEffect(() => {
    let alive = true
    void delivery?.getEnabled().then(value => { if (alive && !touched.current) setEnabled(value) })
    return () => { alive = false }
  }, [delivery])

  const offsetLabel = (value: ReminderDefaultOffset) => t(`preferences.reminders.offset.${value}`)

  return (
    <SettingsSection title={t('settings.section.reminders')} variant="card">
      <SettingsRow
        label={t('preferences.reminders.standard')}
        description={t('preferences.reminders.standardHint')}
        htmlFor="app-behavior-reminder-offset"
        control={
          <Select value={String(offset)} onValueChange={value => setOffset(value === 'off' ? 'off' : Number(value))}>
            <SelectTrigger id="app-behavior-reminder-offset" className="w-auto gap-2">
              <SelectValue>{offsetLabel(offset)}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {OFFSETS.map(value => (
                <SelectItem key={String(value)} value={String(value)}>{offsetLabel(value)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />

      {delivery && (
        <SettingsRow
          label={t('preferences.reminders.device')}
          description={t(`preferences.reminders.deviceHint.${system}`)}
          htmlFor="app-behavior-reminder-device"
          control={
            <Switch
              id="app-behavior-reminder-device"
              checked={enabled}
              onCheckedChange={next => { touched.current = true; setEnabled(next); void delivery.setEnabled(next) }}
            />
          }
        />
      )}

      {delivery && permission && (
        <SettingsRow
          label={t('preferences.reminders.permission')}
          description={t(`preferences.reminders.permissionState.${permission}`)}
          control={permission === 'default' ? (
            <Button variant="outline" onClick={() => { void delivery.requestPermission().then(setPermission) }}>
              {t('reminders.permission.allow')}
            </Button>
          ) : null}
        />
      )}

      {delivery && (
        <SettingsRow
          label={t('preferences.reminders.test')}
          description={t(`preferences.reminders.testHint.${system}`)}
          control={
            <Button
              variant="outline"
              onClick={() => {
                // Die Probe fragt im Browser selbst nach der Erlaubnis — danach stimmt die Zeile darüber.
                void delivery.test().then(() => { if (platform === 'web') setPermission(delivery.getPermission()) })
              }}
            >
              {t('preferences.reminders.testButton')}
            </Button>
          }
        />
      )}
    </SettingsSection>
  )
}
